"use client";

import { useEffect } from "react";
import { X } from "lucide-react";

/**
 * Bottom sheet drawer that matches app theming and container width.
 * - Full width edge-to-edge panel; square edges
 * - Header and content both use .container-app for consistent max-width
 * - Uses global tokens (sheet-bg, backdrop-soft, --border, --card)
 */
export default function DiscussionDrawer({
  open,
  title,
  onClose,
  headerRight = null,
  headerLeft = null,
  children,
  bodyClassName = "h-full overflow-hidden pt-4 pb-6",
  hideHeader = false,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <div
      aria-hidden={!open}
      className={`fixed inset-0 z-[80] transition ${open ? "" : "pointer-events-none"}`}
    >
      {/* Backdrop uses global helper for consistent look */}
      <div
        className={`absolute inset-0 backdrop-strong transition-opacity ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className={`absolute left-0 right-0 sheet-bg
          top-0 bottom-0 sm:top-[var(--site-header-h,56px)]
          flex flex-col
          transition-transform duration-[var(--dur-220)] ease-[var(--ease-standard)]
          w-full max-w-[calc(48rem+2rem)] mx-auto
          ${open ? "translate-y-0" : "translate-y-full"}`}
        role="dialog"
        aria-modal="true"
      >
        {!hideHeader ? (
          <div className="border-b border-[var(--border)]">
            <div className="container-app py-3">
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                <div className="justify-self-start">{headerLeft}</div>
                <div className="justify-self-center text-sm font-medium text-[var(--fg-secondary)] text-center">
                  {title}
                </div>
                <div className="justify-self-end flex items-center gap-3">
                  {headerRight}
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center gap-2 rounded-full border border-[var(--border)] px-3 py-1.5 text-xs font-semibold text-[var(--fg-secondary)] hover:text-[var(--brand)] hover:border-[var(--brand)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--card)]"
                    aria-label="Close panel"
                  >
                    <X className="h-4 w-4" />
                    <span>Close</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Body fills remaining height; descendants manage scrolling */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <div className={bodyClassName}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
